import { GetServerSideProps, NextPage } from 'next'
import styles from '~/components/SignIn/SignIn.module.scss'
import nextI18nextConfig from '~/next-i18next.config'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { useTranslation } from 'next-i18next'
import { signIn } from 'next-auth/react'
import { ChangeEvent, FormEvent, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheck, faTimes } from '@fortawesome/pro-regular-svg-icons'
import { useRouter } from 'next/router'
import { HtmlPageMeta } from '~/components/HtmlPageMeta/HtmlPageMeta'
import { HtmlPageMetaResourceService } from '~/components/HtmlPageMeta/HtmlPageMetaResourceService'
import { HtmlPageMetaContextService } from '~/components/HtmlPageMeta/HtmlPageMetaContextService'
import { HtmlPageMetaContextProps } from '~/components/HtmlPageMeta/HtmlPageMetaContextProps'

interface Props {
  htmlPageMetaContextProps: HtmlPageMetaContextProps
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  const locale = context.locale ? context.locale : nextI18nextConfig.i18n.defaultLocale
  const htmlPageMetaContextService = new HtmlPageMetaContextService(context)

  return {
    props: {
      htmlPageMetaContextProps: htmlPageMetaContextService.getProperties(),
      ...await serverSideTranslations(locale, [
        'user_tos',
        'app_menu',
        'footer',
        'sign_in',
        'wallet_error_modal',
      ]),
    },
  }
}

const SignInPage: NextPage<Props> = ({ htmlPageMetaContextProps }) => {
  const { t } = useTranslation('sign_in')
  const [email, setEmail] = useState<string>('')
  const [isEmailValid, setIsEmailValid] = useState<boolean>(false)
  const router = useRouter()
  const locale = router.locale ?? nextI18nextConfig.i18n.defaultLocale
  const htmlPageMetaUrlProps = (
    new HtmlPageMetaResourceService(t('sign_in_title'), t('sign_in_description'))
  ).getProperties()
  const htmlPageMetaProps = { ...htmlPageMetaContextProps, ...htmlPageMetaUrlProps }
  const query = router.query
  const callbackUrl = query.callbackUrl ?? '/'

  const onEmailInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value)

    if (event.target.value === '') {
      return
    }

    const emailRegex = /^\w+([.+-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/
    const emailMatch: RegExpMatchArray | null = event.target.value.match(emailRegex)

    setIsEmailValid(emailMatch !== null)
  }

  let emailValidationText = null
  let emailValidationIcon = null

  if (email.length) {
    if (isEmailValid) {
      emailValidationText = (
        <span
          data-testid='valid-email'
          className={ styles.signIn__validEmail }
        >
          { t('valid_email') }
        </span>
      )

      emailValidationIcon = (
        <FontAwesomeIcon
          className={ `
            ${styles.signIn__inputIcon}
            ${styles.signIn__inputIcon_valid}
          ` }
          icon={ faCheck }
        />
      )
    } else {
      emailValidationText = (
        <span
          data-testid='non-valid-email'
          className={ styles.signIn__nonValidEmail }
        >
          { t('non_valid_email') }
        </span>
      )
      emailValidationIcon = (
        <FontAwesomeIcon
          className={ `
            ${styles.signIn__inputIcon}
            ${styles.signIn__inputIcon_nonValid}
          ` }
          icon={ faTimes }
        />
      )
    }
  }

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault()

    const result = await signIn('email', {
      redirect: false,
      email,
      callbackUrl: `/${locale}${callbackUrl}`,
    })

    if (result?.ok && result?.error !== null) {
      await router.replace({
        pathname: '/auth/error',
        query: {
          error: result.error,
          callbackUrl,
        },
      })
    }

    if (result?.ok && result?.error === null) {
      await router.push({
        pathname: 'verify-request',
        query: {
          callbackUrl,
        },
      })
    }
  }

  return (
    <>
      <HtmlPageMeta { ...htmlPageMetaProps } />

      <form
        onSubmit={ onSubmit }
        className={ styles.signIn__form }
      >
        <h1
          className={ styles.signIn__title }
        >
          { t('title') }
        </h1>
        <label
          className={ styles.signIn__label }
        >
          { t('label') }
          <div
            className={ styles.signIn__inputWrapper }
          >
            <input
              type='email'
              name='email'
              data-testid='input'
              placeholder={ t('input_placeholder') }
              className={ `
              ${styles.signIn__input}
              ${email.length > 0 && styles.signIn__input_filled}
            ` }
              onChange={ onEmailInputChange }
              defaultValue={ email }
            />
            { emailValidationIcon }
          </div>

          { emailValidationText }
        </label>
        <button
          data-testid='sign-in-button'
          disabled={ !email.length || !isEmailValid }
          className={ `
            ${styles.signIn__button}
            ${(!email.length || !isEmailValid) && styles.signIn__button_disabled}
          ` }
        >
          { t('button') }
        </button>
      </form>
    </>
  )
}

export default SignInPage
